import React, { useRef, useCallback } from 'react';
import {
  Typography, Button, Box, TextField, Alert, CircularProgress,
  Tabs, Tab, List, ListItemButton, ListItemAvatar, ListItemText,
  Avatar, IconButton, ImageList, ImageListItem, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import BarcodeScanner from '../../components/BarcodeScanner';
import GroceryForm from '../../forms/GroceryForm';
import { apiFetch } from '../../apiFetch';
import { haptics } from '../../utils/haptics';
import { supportsCameraCapture } from '../../utils/camera';
import {
  Grocery, emptyGroceryFormData, GroceryCatalogItem, BarcodeLookupResponse,
} from '../../types/Grocery';
import { FindGroceryStepProps } from './types';
import TabPanel from './TabPanel';

const FindGroceryStep: React.FC<FindGroceryStepProps> = ({
  findTabValue,
  setFindTabValue,
  searchQuery,
  setSearchQuery,
  searchResults,
  setSearchResults,
  searchLoading,
  setSearchLoading,
  barcodeInput,
  setBarcodeInput,
  barcodeLoading,
  setBarcodeLoading,
  scannerResetTrigger,
  setScannerResetTrigger,
  formData,
  setFormData,
  formErrors,
  setFormErrors,
  selectedCatalog,
  setSelectedCatalog,
  selectedImageType,
  setSelectedImageType,
  uploadingCustom,
  setUploadingCustom,
  customImagePreview,
  setCustomImagePreview,
  useCustomImage,
  setUseCustomImage,
  availableGroceries,
  availableGroceriesLoading,
  grocery,
  setGrocery,
  error,
  setError,
  loading,
  setLoading,
  onClose,
  apiUrl,
  setWasNewGrocery,
  setActiveStep,
  existingGroceries,
  prices,
  setPrice,
  setIsDiscounted,
  setPriceBeforeDiscount,
  setWasAddedToList,
  open,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [hasCameraCapture, setHasCameraCapture] = React.useState(false);

  React.useEffect(() => {
    supportsCameraCapture().then(setHasCameraCapture);
  }, []);

  const handleFindTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setFindTabValue(newValue);
    setError(null);
  };

  // Get image URLs for a given type
  const getSelectedImageUrls = (catalog: GroceryCatalogItem, type: 'front' | 'ingredients' | 'nutrition') => {
    if (type === 'front') {
      return {
        image_url: catalog.image_front_url || '',
        thumbnail_url: catalog.image_front_thumb_url || '',
        icon_url: catalog.image_front_icon_url || '',
      };
    } else if (type === 'ingredients') {
      return {
        image_url: catalog.image_ingredients_url || '',
        thumbnail_url: catalog.image_ingredients_thumb_url || '',
        icon_url: '',
      };
    } else {
      return {
        image_url: catalog.image_nutrition_url || '',
        thumbnail_url: catalog.image_nutrition_thumb_url || '',
        icon_url: '',
      };
    }
  };

  // Prefill form from catalog data
  const prefillFormFromCatalog = useCallback((catalog: GroceryCatalogItem) => {
    let category = '';
    if (catalog.categories) {
      const cats = catalog.categories.split(',').map((c) => c.trim());
      const englishCat = cats.find((c) => !c.includes(':')) || cats[0];
      if (englishCat) {
        category = englishCat
          .replace(/^[a-z]{2}:/, '')
          .split('-')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
      }
    }

    const imageUrls = getSelectedImageUrls(catalog, 'front');

    setFormData({
      name: catalog.name || '',
      brand: catalog.brand || '',
      category: category,
      description: catalog.generic_name || '',
      barcode: catalog.barcode || '',
      unit: '',
      packaging_size: catalog.quantity || '',
      image: imageUrls.image_url,
      image_thumbnail: imageUrls.thumbnail_url,
      image_icon: imageUrls.icon_url,
      notes: '',
    });
    setFormErrors({});
  }, [setFormData, setFormErrors]);

  // After grocery is found or created, check if it's in the shopping list
  const proceedAfterGroceryFound = (foundGrocery: Grocery) => {
    const inList = existingGroceries.some(
      (item) => item.grocery === foundGrocery.id
    );

    const existingPrice = prices.get(foundGrocery.id);
    if (existingPrice) {
      setPrice(existingPrice.price);
      setIsDiscounted(existingPrice.is_discounted);
      if (existingPrice.price_before_discount) {
        setPriceBeforeDiscount(existingPrice.price_before_discount);
      }
    }

    if (inList) {
      setWasAddedToList(false);
      setActiveStep(2);
    } else {
      setActiveStep(1);
    }
  };

  // Handle selecting an existing grocery from Select tab
  const handleSelectExistingGrocery = (selectedGrocery: Grocery) => {
    haptics.medium();
    setGrocery(selectedGrocery);
    setWasNewGrocery(false);
    setActiveStep(1);
  };

  // Search functionality
  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return;
    setSearchLoading(true);
    setError(null);

    try {
      const response = await apiFetch(
        `${apiUrl}/api/groceries/search/?q=${encodeURIComponent(searchQuery.trim())}`
      );
      setSearchResults(response.results || []);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearchResultClick = async (result: { barcode: string; name: string }) => {
    setSearchLoading(true);
    setError(null);

    try {
      const existingGroceriesList = await apiFetch(`${apiUrl}/api/groceries/`);
      const existingGrocery = existingGroceriesList.find(
        (g: Grocery) => g.barcode === result.barcode
      );

      if (existingGrocery) {
        haptics.medium();
        setGrocery(existingGrocery);
        setWasNewGrocery(false);
        proceedAfterGroceryFound(existingGrocery);
      } else {
        const response: BarcodeLookupResponse = await apiFetch(
          `${apiUrl}/api/groceries/barcode/${result.barcode}/`
        );

        if (response.found && response.data) {
          haptics.medium();
          setSelectedCatalog(response.data);
          setSelectedImageType('front');
          prefillFormFromCatalog(response.data);
        } else {
          haptics.error();
          setError('Product not found');
        }
      }
    } catch (err: any) {
      haptics.error();
      setError(err.message || 'Failed to look up product');
    } finally {
      setSearchLoading(false);
    }
  };

  // Barcode functionality
  const handleBarcodeDetected = async (barcode: string) => {
    haptics.heavy();
    setBarcodeInput(barcode);
    await lookupBarcode(barcode);
  };

  const handleBarcodeLookup = async () => {
    if (!barcodeInput.trim()) return;
    await lookupBarcode(barcodeInput.trim());
  };

  const lookupBarcode = async (barcode: string) => {
    setBarcodeLoading(true);
    setError(null);

    try {
      const userGroceries = await apiFetch(`${apiUrl}/api/groceries/`);
      const existingGrocery = userGroceries.find(
        (g: Grocery) => g.barcode === barcode
      );

      if (existingGrocery) {
        haptics.medium();
        setGrocery(existingGrocery);
        setWasNewGrocery(false);
        proceedAfterGroceryFound(existingGrocery);
        return;
      }

      const response: BarcodeLookupResponse = await apiFetch(
        `${apiUrl}/api/groceries/barcode/${barcode}/`
      );

      if (response.found && response.data) {
        haptics.medium();
        setSelectedCatalog(response.data);
        setSelectedImageType('front');
        prefillFormFromCatalog(response.data);
      } else {
        haptics.warning();
        setFormData({ ...emptyGroceryFormData, barcode });
        setFindTabValue(3);
        setError('Product not found in database. Please enter details manually.');
      }
    } catch (err: any) {
      haptics.error();
      setError(err.message || 'Failed to look up barcode');
    } finally {
      setBarcodeLoading(false);
    }
  };

  // Handle image selection from catalog
  const handleImageSelect = (type: 'front' | 'ingredients' | 'nutrition') => {
    if (!selectedCatalog) return;

    setSelectedImageType(type);
    setUseCustomImage(false);
    setCustomImagePreview(null);

    const imageUrls = getSelectedImageUrls(selectedCatalog, type);
    setFormData((prev) => ({
      ...prev,
      image: imageUrls.image_url,
      image_thumbnail: imageUrls.thumbnail_url,
      image_icon: imageUrls.icon_url,
    }));
  };

  // Handle custom image upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image file is too large. Maximum size is 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCustomImagePreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploadingCustom(true);
    setError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const makeUploadRequest = async (token: string) => {
        return fetch(`${apiUrl}/api/groceries/upload-image/`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload,
        });
      };

      let response = await makeUploadRequest(localStorage.getItem('access_token') || '');

      if (response.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const refreshResponse = await fetch(`${apiUrl}/api/token/refresh/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: refreshToken }),
          });

          if (refreshResponse.ok) {
            const tokens = await refreshResponse.json();
            localStorage.setItem('access_token', tokens.access);
            response = await makeUploadRequest(tokens.access);
          } else {
            throw new Error('Session expired. Please log in again.');
          }
        } else {
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const imageData = await response.json();

      setFormData((prev) => ({
        ...prev,
        image: imageData.image_url,
        image_thumbnail: imageData.thumbnail_url,
        image_icon: imageData.icon_url,
      }));

      setUseCustomImage(true);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image.');
      setCustomImagePreview(null);
    } finally {
      setUploadingCustom(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  // Clear custom image
  const handleClearCustomImage = () => {
    setCustomImagePreview(null);
    setUseCustomImage(false);

    if (selectedCatalog) {
      const imageUrls = getSelectedImageUrls(selectedCatalog, selectedImageType);
      setFormData((prev) => ({
        ...prev,
        image: imageUrls.image_url,
        image_thumbnail: imageUrls.thumbnail_url,
        image_icon: imageUrls.icon_url,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        image: '',
        image_thumbnail: '',
        image_icon: '',
      }));
    }
  };

  // Manual form handling
  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateGrocery = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const payload: any = {
        name: formData.name.trim(),
        category: formData.category.trim(),
      };

      if (formData.brand.trim()) payload.brand = formData.brand.trim();
      if (formData.description.trim()) payload.description = formData.description.trim();
      if (formData.barcode.trim()) payload.barcode = formData.barcode.trim();
      if (formData.unit.trim()) payload.unit = formData.unit.trim();
      if (formData.packaging_size.trim()) payload.packaging_size = formData.packaging_size.trim();
      if (formData.image) payload.image = formData.image;
      if (formData.image_thumbnail) payload.image_thumbnail = formData.image_thumbnail;
      if (formData.image_icon) payload.image_icon = formData.image_icon;
      if (formData.notes.trim()) payload.notes = formData.notes.trim();

      if (selectedCatalog) {
        payload.catalog_item_id = selectedCatalog.id;
      }

      const newGrocery = await apiFetch(`${apiUrl}/api/groceries/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      haptics.success();
      setGrocery(newGrocery);
      setWasNewGrocery(true);
      proceedAfterGroceryFound(newGrocery);
    } catch (err: any) {
      haptics.error();
      setError(err.message || 'Failed to create grocery');
    } finally {
      setLoading(false);
    }
  };

  // Render catalog image selection
  const renderCatalogImages = () => {
    if (!selectedCatalog) return null;

    const allImages: { type: 'front' | 'ingredients' | 'nutrition'; url: string | null | undefined }[] = [
      { type: 'front' as const, url: selectedCatalog.image_front_thumb_url },
      { type: 'ingredients' as const, url: selectedCatalog.image_ingredients_thumb_url },
      { type: 'nutrition' as const, url: selectedCatalog.image_nutrition_thumb_url },
    ];
    const images = allImages.filter(img => img.url);

    if (images.length === 0 && !customImagePreview) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select Image
        </Typography>
        <ImageList cols={Math.min(images.length + 1, 4)} rowHeight={100} gap={8}>
          {images.map((img) => (
            <ImageListItem
              key={img.type}
              onClick={() => handleImageSelect(img.type)}
              sx={{
                cursor: 'pointer',
                border: selectedImageType === img.type && !useCustomImage ? '3px solid' : '1px solid',
                borderColor: selectedImageType === img.type && !useCustomImage ? 'primary.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <img
                src={img.url!}
                alt={img.type}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {selectedImageType === img.type && !useCustomImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    p: 0.25,
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 18 }} />
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  textAlign: 'center',
                  py: 0.25,
                  textTransform: 'capitalize',
                }}
              >
                {img.type}
              </Typography>
            </ImageListItem>
          ))}

          {customImagePreview && (
            <ImageListItem
              sx={{
                border: useCustomImage ? '3px solid' : '1px solid',
                borderColor: useCustomImage ? 'primary.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <img
                src={customImagePreview}
                alt="Custom"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {useCustomImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    p: 0.25,
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 18 }} />
                </Box>
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearCustomImage();
                }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: 2,
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 20,
                  height: 20,
                  fontSize: 14,
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                ×
              </IconButton>
            </ImageListItem>
          )}
        </ImageList>

        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingCustom}
          >
            Upload
          </Button>
          {hasCameraCapture && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddAPhotoIcon />}
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploadingCustom}
            >
              Camera
            </Button>
          )}
          {uploadingCustom && <CircularProgress size={20} />}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Scan, search, or manually enter a grocery item.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={findTabValue}
          onChange={handleFindTabChange}
          variant="fullWidth"
        >
          <Tab icon={<QrCodeScannerIcon />} iconPosition="start" label="Scan" />
          <Tab icon={<SearchIcon />} iconPosition="start" label="Search" />
          <Tab
            icon={<ListAltIcon />}
            iconPosition="start"
            label="Select"
            disabled={availableGroceries.length === 0 && !availableGroceriesLoading}
          />
          <Tab icon={<EditIcon />} iconPosition="start" label="Manual" />
        </Tabs>
      </Box>

      {/* Scan Tab */}
      <TabPanel value={findTabValue} index={0}>
        {!selectedCatalog && (
          <>
            <BarcodeScanner
              onDetected={handleBarcodeDetected}
              onError={(err) => setError(err)}
              active={open && findTabValue === 0 && !selectedCatalog}
              resetTrigger={scannerResetTrigger}
            />
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                or enter manually
              </Typography>
            </Divider>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Barcode / PLU"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBarcodeLookup()}
                size="small"
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleBarcodeLookup}
                disabled={barcodeLoading || !barcodeInput.trim()}
              >
                {barcodeLoading ? <CircularProgress size={20} /> : 'Look Up'}
              </Button>
            </Box>
          </>
        )}

        {selectedCatalog && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Found: {selectedCatalog.name} {selectedCatalog.brand && `- ${selectedCatalog.brand}`}
              <Button
                size="small"
                sx={{ ml: 2 }}
                onClick={() => {
                  setSelectedCatalog(null);
                  setFormData(emptyGroceryFormData);
                  setBarcodeInput('');
                  setSelectedImageType('front');
                  setCustomImagePreview(null);
                  setUseCustomImage(false);
                  setScannerResetTrigger(prev => prev + 1);
                }}
              >
                Scan Another
              </Button>
            </Alert>

            {renderCatalogImages()}

            <Divider sx={{ my: 2 }} />

            <GroceryForm
              formData={formData}
              errors={formErrors}
              onChange={handleFormChange}
              compact
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleCreateGrocery}
                disabled={loading || uploadingCustom}
              >
                {loading ? <CircularProgress size={24} /> : 'Add Grocery'}
              </Button>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Search Tab */}
      <TabPanel value={findTabValue} index={1}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Search products"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            fullWidth
            size="small"
          />
          <Button
            variant="outlined"
            onClick={handleSearch}
            disabled={searchLoading || searchQuery.trim().length < 2}
          >
            {searchLoading ? <CircularProgress size={20} /> : 'Search'}
          </Button>
        </Box>

        {searchResults.length > 0 && !selectedCatalog && (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {searchResults.map((result) => (
              <ListItemButton
                key={result.barcode}
                onClick={() => handleSearchResultClick(result)}
                disabled={searchLoading}
              >
                <ListItemAvatar>
                  <Avatar
                    src={result.thumbnail_url || undefined}
                    variant="rounded"
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={result.name}
                  secondary={[result.brand, result.quantity].filter(Boolean).join(' | ')}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {selectedCatalog && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Selected: {selectedCatalog.name} {selectedCatalog.brand && `- ${selectedCatalog.brand}`}
              <Button
                size="small"
                sx={{ ml: 2 }}
                onClick={() => {
                  setSelectedCatalog(null);
                  setFormData(emptyGroceryFormData);
                  setSelectedImageType('front');
                  setCustomImagePreview(null);
                  setUseCustomImage(false);
                }}
              >
                Clear
              </Button>
            </Alert>

            {renderCatalogImages()}

            <Divider sx={{ my: 2 }} />

            <GroceryForm
              formData={formData}
              errors={formErrors}
              onChange={handleFormChange}
              compact
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleCreateGrocery}
                disabled={loading || uploadingCustom}
              >
                {loading ? <CircularProgress size={24} /> : 'Add Grocery'}
              </Button>
            </Box>
          </Box>
        )}
      </TabPanel>

      {/* Select Tab - existing groceries not in list */}
      <TabPanel value={findTabValue} index={2}>
        {availableGroceriesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : availableGroceries.length === 0 ? (
          <Alert severity="info">
            All your groceries are already in this shopping list.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a grocery from your database ({availableGroceries.length} available):
            </Typography>
            <List sx={{ maxHeight: 350, overflow: 'auto' }}>
              {availableGroceries.map((g) => (
                <ListItemButton
                  key={g.id}
                  onClick={() => handleSelectExistingGrocery(g)}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={g.image_icon || undefined}
                      variant="rounded"
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={g.name}
                    secondary={[g.brand, g.category, g.packaging_size].filter(Boolean).join(' | ')}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </TabPanel>

      {/* Manual Tab */}
      <TabPanel value={findTabValue} index={3}>
        <GroceryForm
          formData={formData}
          errors={formErrors}
          onChange={handleFormChange}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleCreateGrocery}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Grocery'}
          </Button>
        </Box>
      </TabPanel>

      {/* Cancel button for step 1 */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
      </Box>
    </>
  );
};

export default FindGroceryStep;
